import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, FileText, Loader2, Archive, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

export default function ArchivedStep({ project }) {
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!project?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await axios.get(`${API}/projects/${project.id}/archived-submittals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setArchived(res.data?.archived || []);
      } catch (e) {
        console.error('Failed to load archived:', e);
        setArchived([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project?.id]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="archived-step">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Archive className="h-5 w-5 text-primary" />
          Archived Submittals
        </h3>
        <p className="text-sm text-muted-foreground">Previous versions of submittals. Older versions are automatically archived when a new version is created.</p>
      </div>

      {archived.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Archive className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No archived submittals yet. Previous versions will appear here after you create new versions in the Annotate step.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {archived.map((item, i) => {
            const url = item.download_url
              ? (item.download_url.startsWith('http') ? item.download_url : `${BACKEND_URL}${item.download_url}`)
              : '#';
            const filename = item.filename || 'archived_submittal.pdf';
            const dlUrl = url !== '#' ? `${url}${url.includes('?') ? '&' : '?'}dl=${encodeURIComponent(filename)}` : '#';

            return (
              <Card key={i} data-testid={`archived-item-${i}`}>
                <CardContent className="py-3 flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.filename}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Unknown date'}</span>
                      <span>Version {item.version}</span>
                    </div>
                  </div>
                  <a
                    href={dlUrl}
                    download={filename}
                    data-testid={`download-archived-${i}`}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
